import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";
import { safeOnboardingNext } from "../lib/onboardingNext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import CloudflareTurnstile, { TURNSTILE_ENABLED } from "../components/security/CloudflareTurnstile";
import {
  STAFF_CODE_ENDPOINT,
  codeSendEndpointForLoginResult,
} from "../components/security/loginTurnstileFlow";
import { ChartBar, EnvelopeSimple, Lock, ShieldCheck, GoogleLogo, ArrowLeft, CircleNotch, Key, Eye, EyeSlash } from "@phosphor-icons/react";

// FASE 1 — Admin Central. El Founder aterriza en la pantalla de elección
// (Admin Central | Call Center) justo después de loguearse; cualquier otro
// usuario sigue yendo directo a /dashboard como siempre.
const FOUNDER_EMAIL = "consulting@vzwhelpscenter.com";
function postLoginPath(loggedInEmail) {
  return (loggedInEmail || "").toLowerCase() === FOUNDER_EMAIL ? "/start" : "/dashboard";
}

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [step, setStep] = useState("login"); // "login" | "2fa" | "2fa_prompt" | "totp" | "admin_choice" | "device_destination_choice" | "device_approval"
  const [maskedEmail, setMaskedEmail] = useState("");
  const [trustDevice, setTrustDevice] = useState(true);
  const [sendsRemaining, setSendsRemaining] = useState(2);
  const [adminOptions, setAdminOptions] = useState([]);
  const [codeSendEndpoint, setCodeSendEndpoint] = useState(STAFF_CODE_ENDPOINT);
  const [error, setError] = useState("");
  const [licenseBlocked, setLicenseBlocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  // FASE A · Device approval state
  const [approvalToken, setApprovalToken] = useState("");
  const [approvalExpiresAt, setApprovalExpiresAt] = useState("");
  const [approvalPrimary, setApprovalPrimary] = useState("");
  const [approvalSecondary, setApprovalSecondary] = useState("");
  const [approvalGoesToFounder, setApprovalGoesToFounder] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState("pending");
  // FASE A · Destination choice state (iter 134)
  const [choiceToken, setChoiceToken] = useState("");
  const [choicePrimaryAvailable, setChoicePrimaryAvailable] = useState(true);
  const [choiceSecondaryAvailable, setChoiceSecondaryAvailable] = useState(false);
  const [choicePrimarySelected, setChoicePrimarySelected] = useState(true);
  const [choiceSecondarySelected, setChoiceSecondarySelected] = useState(false);
  const [choiceSubmitting, setChoiceSubmitting] = useState(false);
  const { login, checkAuth } = useAuth();
  const navigate = useNavigate();

  // N1 (validador único de next, ver lib/onboardingNext.js) -- si el
  // cliente llegó aquí desde una sesión vencida a mitad del wizard de
  // onboarding Beauty (?next=/onboarding/<token>, ver lib/api.js), se
  // calcula UNA sola vez al montar: ni la URL ni el token cambian durante
  // el ciclo de vida de este formulario. Un `next` ausente o inválido
  // (incluida cualquier URL externa/maliciosa) es indistinguible de "no
  // hay next" -- se ignora silenciosamente, nunca se expone el motivo del
  // rechazo al usuario.
  const safeNext = useMemo(
    () => safeOnboardingNext(new URLSearchParams(window.location.search).get("next")),
    []
  );
  const goAfterLogin = useCallback(
    (email) => navigate(safeNext || postLoginPath(email)),
    [navigate, safeNext]
  );
  const forgotPasswordHref = safeNext ? `/forgot-password?next=${encodeURIComponent(safeNext)}` : "/forgot-password";

  // If the user was force-logged-out due to license expiry, restore the
  // blocked banner so they immediately understand why they're back on /login.
  useEffect(() => {
    try {
      const msg = sessionStorage.getItem("license_expired_msg");
      if (msg) {
        setLicenseBlocked(true);
        setError(msg);
        sessionStorage.removeItem("license_expired_msg");
      }
    } catch { /* sessionStorage unavailable */ }
  }, []);

  // FASE A iter 134 · Submit destination choice → server sends email & returns approval_token
  const submitDestinationChoice = async () => {
    if (!choicePrimarySelected && !choiceSecondarySelected) {
      setError("Selecciona al menos un destino para enviar el link de aprobación.");
      return;
    }
    setChoiceSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/auth/request-device-approval-link", {
        choice_token: choiceToken,
        send_primary: choicePrimarySelected,
        send_secondary: choiceSecondarySelected,
      });
      setApprovalToken(data.approval_token);
      setApprovalExpiresAt(data.expires_at);
      setApprovalStatus("pending");
      setStep("device_approval");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "No pudimos enviar el correo. Intenta de nuevo.");
    } finally { setChoiceSubmitting(false); }
  };


  // FASE A · Poll device approval status every 3s while waiting for founder
  // to click the email link. On "approved" we automatically complete the
  // login via /auth/complete-after-approval/{token}.
  useEffect(() => {
    if (step !== "device_approval" || !approvalToken) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/security/approval-status/${approvalToken}`);
        if (cancelled) return;
        if (data.status === "approved") {
          setApprovalStatus("approved");
          // Auto-complete login
          try {
            const { data: u } = await api.post(`/auth/complete-after-approval/${approvalToken}`);
            if (u.role) {
              await checkAuth();
              goAfterLogin(u.email);
            }
          } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || "No pudimos completar el login. Intenta de nuevo.");
            setStep("login");
          }
        } else if (data.status === "denied") {
          setApprovalStatus("denied");
        } else if (data.status === "expired" || data.status === "not_found") {
          setApprovalStatus("expired");
        }
      } catch { /* network blip — keep polling */ }
    };
    tick();
    const id = setInterval(tick, 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, [step, approvalToken, checkAuth, navigate, goAfterLogin]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLicenseBlocked(false);
    if (TURNSTILE_ENABLED && !turnstileToken) {
      setError("Confirma que eres una persona antes de continuar.");
      return;
    }
    setLoading(true);
    try {
      if (step === "2fa" || step === "totp") {
        const { data } = await api.post("/auth/login", {
          email, password, verification_code: verificationCode, trust_device: trustDevice,
          turnstile_token: turnstileToken || undefined,
        });
        if (data.requires_device_destination_choice) {
          setStep("device_destination_choice");
          setChoiceToken(data.choice_token);
          setApprovalPrimary(data.primary_email_masked || "");
          setApprovalSecondary(data.secondary_email_masked || "");
          setChoicePrimaryAvailable(!!data.primary_available);
          setChoiceSecondaryAvailable(!!data.secondary_available);
          // Pre-select primary by default
          setChoicePrimarySelected(!!data.primary_available);
          setChoiceSecondarySelected(false);
        } else if (data.requires_device_approval) {
          setStep("device_approval");
          setApprovalToken(data.approval_token);
          setApprovalExpiresAt(data.expires_at);
          setApprovalPrimary(data.primary_email_masked || "");
          setApprovalSecondary(data.secondary_email_masked || "");
          setApprovalGoesToFounder(!!data.approval_goes_to_founder);
          setApprovalStatus("pending");
        } else if (data.role) {
          await checkAuth();
          goAfterLogin(data.email);
        }
      } else {
        setAdminOptions([]);
        setCodeSendEndpoint(STAFF_CODE_ENDPOINT);
        const { data } = await api.post("/auth/login", {
          email,
          password,
          turnstile_token: turnstileToken || undefined,
        });
        if (data.requires_device_destination_choice) {
          // Same handling as the 2fa/totp branch above (line ~126) -- a
          // brand-new/untrusted device can hit this on the very FIRST
          // submit too, before any 2FA code is ever entered. This branch
          // was previously missing here, so this exact response (200 +
          // requires_device_destination_choice) silently matched none of
          // the conditions below and left the user stuck on "Ingresando...".
          setStep("device_destination_choice");
          setChoiceToken(data.choice_token);
          setApprovalPrimary(data.primary_email_masked || "");
          setApprovalSecondary(data.secondary_email_masked || "");
          setChoicePrimaryAvailable(!!data.primary_available);
          setChoiceSecondaryAvailable(!!data.secondary_available);
          setChoicePrimarySelected(!!data.primary_available);
          setChoiceSecondarySelected(false);
        } else if (data.requires_admin_verify) {
          setCodeSendEndpoint(codeSendEndpointForLoginResult(data));
          setStep("admin_choice");
          setAdminOptions(data.options || []);
          setMaskedEmail(data.auth_email_masked || "***");
        } else if (data.requires_2fa) {
          setCodeSendEndpoint(codeSendEndpointForLoginResult(data));
          if (data.email_sent === false) {
            setStep("2fa_prompt");
          } else {
            setStep("2fa");
          }
          setMaskedEmail(data.auth_email_masked || "***");
          setSendsRemaining(data.sends_remaining ?? 2);
        } else if (data.requires_totp) {
          setStep("totp");
        } else if (data.role) {
          await checkAuth();
          goAfterLogin(data.email);
        }
      }
    } catch (err) {
      if (err?.response?.status === 402) {
        setLicenseBlocked(true);
        setError("");
      } else {
        setError(formatApiError(err.response?.data?.detail) || err.message);
      }
    } finally {
      setLoading(false);
      if (TURNSTILE_ENABLED) {
        setTurnstileToken("");
        setTurnstileResetKey((value) => value + 1);
      }
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleResendCode = async () => {
    setError("");
    if (TURNSTILE_ENABLED && !turnstileToken) {
      setError("Confirma que eres una persona antes de continuar.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(codeSendEndpoint, {
        email,
        password,
        turnstile_token: turnstileToken || undefined,
      });
      setMaskedEmail(data.auth_email_masked || maskedEmail);
      setSendsRemaining(data.sends_remaining ?? 0);
      setStep("2fa");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
      if (TURNSTILE_ENABLED) {
        setTurnstileToken("");
        setTurnstileResetKey((value) => value + 1);
      }
    }
  };

  const handleStaffSendCode = async () => {
    setError("");
    if (TURNSTILE_ENABLED && !turnstileToken) {
      setError("Confirma que eres una persona antes de continuar.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/staff-send-code", {
        email,
        password,
        turnstile_token: turnstileToken || undefined,
      });
      setMaskedEmail(data.auth_email_masked || maskedEmail);
      setSendsRemaining(data.sends_remaining ?? 0);
      setStep("2fa");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
      if (TURNSTILE_ENABLED) {
        setTurnstileToken("");
        setTurnstileResetKey((value) => value + 1);
      }
    }
  };

  const handleAdminChooseEmail = async () => {
    setError("");
    if (TURNSTILE_ENABLED && !turnstileToken) {
      setError("Confirma que eres una persona antes de continuar.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/admin-send-code", {
        email,
        password,
        turnstile_token: turnstileToken || undefined,
      });
      setMaskedEmail(data.auth_email_masked || maskedEmail);
      setSendsRemaining(data.sends_remaining ?? 0);
      setStep("2fa");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
      if (TURNSTILE_ENABLED) {
        setTurnstileToken("");
        setTurnstileResetKey((value) => value + 1);
      }
    }
  };

  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const handleMouseMove = (e) => {
    const { clientX, clientY, currentTarget } = e;
    const { width, height } = currentTarget.getBoundingClientRect();
    setMousePos({ x: (clientX / width - 0.5) * 24, y: (clientY / height - 0.5) * 24 });
  };

  return (
    <div className="min-h-screen flex relative overflow-hidden" data-testid="login-page" onMouseMove={handleMouseMove} style={{ background: "#000000" }}>
      {/* Subtle premium ambient glow */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 30% 35%, rgba(234,138,40,0.05) 0%, transparent 50%), radial-gradient(ellipse at 75% 70%, rgba(234,138,40,0.025) 0%, transparent 45%)" }} />

      {/* Background watermark — same parallax style as SaaS landing */}
      <div
        className="fixed pointer-events-none select-none"
        style={{
          width: "min(45vh, 45vw)",
          height: "min(45vh, 45vw)",
          top: "50%",
          left: "50%",
          opacity: 0.07,
          transform: `translate(-50%, -50%) translate(${mousePos.x}px, ${mousePos.y}px)`,
          transition: "transform 0.7s cubic-bezier(0.25,0.46,0.45,0.94)",
          zIndex: 1,
        }}
      >
        <img src="/brand/commandix-isotipo-cables.png" alt="" className="w-full h-full object-contain" draggable="false" />
      </div>

      {/* LEFT SIDE — Official COMMANDIX CRM branding */}
      <div className="hidden lg:flex lg:w-[50%] relative items-center justify-center" style={{ zIndex: 2 }}>
        <img
          src="/brand/commandix-logo-tight.png"
          alt="Commandix CRM"
          draggable="false"
          className="object-contain"
          style={{ width: "540px", filter: "drop-shadow(0 0 80px rgba(234,138,40,0.12))" }}
          data-testid="login-branding-logo"
        />
      </div>

      {/* RIGHT SIDE — Login panel, integrated glass floating feel */}
      <div className="flex-1 flex items-center justify-center px-8 lg:px-16 relative" style={{ zIndex: 2 }}>
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="lg:hidden mb-8 flex justify-center">
            <img src="/brand/commandix-logo-tight.png" alt="Commandix CRM" className="h-16 object-contain" data-testid="login-mobile-branding" />
          </div>

          {/* Glass login panel — no harsh box borders */}
          <div className="rounded-2xl p-8 space-y-6" style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", border: "1px solid rgba(255,255,255,0.04)", boxShadow: "0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.03)" }}>
            <div className="text-center space-y-1">
              <h2 className="text-2xl font-bold font-[Manrope] text-white">
                {step === "admin_choice" ? "Verificacion" : step === "2fa_prompt" ? "Verificacion Requerida" : step === "2fa" ? "Verificacion" : step === "totp" ? "Authenticator" : "Iniciar Sesion"}
              </h2>
              <p className="text-sm text-white/40">
                {step === "admin_choice"
                  ? "Donde desea verificar su acceso?"
                  : step === "2fa_prompt"
                  ? "Su dispositivo requiere verificacion de seguridad"
                  : step === "2fa"
                  ? `Ingrese el codigo enviado a ${maskedEmail}`
                  : step === "totp"
                  ? "Ingrese el codigo de Google Authenticator"
                  : "Ingrese sus credenciales para acceder al sistema"}
              </p>
            </div>
            <div>
            {step === "admin_choice" ? (
              <div className="space-y-4" data-testid="admin-choice">
                {error && <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm">{error}</div>}
                <div className="p-4 rounded-lg bg-primary/10 border border-primary/20 text-center">
                  <ShieldCheck size={32} weight="duotone" className="text-primary mx-auto mb-2" />
                  <p className="text-sm font-semibold">Verificacion de seguridad</p>
                  <p className="text-xs text-muted-foreground mt-1">Seleccione como desea verificar su acceso</p>
                </div>
                {adminOptions.includes("totp") && (
                  <Button variant="outline" className="w-full h-16 justify-start gap-4 border-border hover:border-primary/40 hover:bg-primary/5" onClick={() => { setStep("totp"); setVerificationCode(""); }} data-testid="choose-totp">
                    <div className="p-2 rounded-lg bg-purple-500/15"><Key size={22} className="text-purple-400" /></div>
                    <div className="text-left"><p className="font-semibold text-sm">Google Authenticator</p><p className="text-xs text-muted-foreground">Codigo de 6 digitos desde tu app</p></div>
                  </Button>
                )}
                {adminOptions.includes("email") && (
                  <Button variant="outline" className="w-full h-16 justify-start gap-4 border-border hover:border-primary/40 hover:bg-primary/5" onClick={handleAdminChooseEmail} disabled={loading || (TURNSTILE_ENABLED && !turnstileToken)} data-testid="choose-email">
                    <div className="p-2 rounded-lg bg-cyan-500/15"><EnvelopeSimple size={22} className="text-cyan-400" /></div>
                    <div className="text-left"><p className="font-semibold text-sm">Codigo por Email</p><p className="text-xs text-muted-foreground">Enviar codigo a {maskedEmail}</p></div>
                  </Button>
                )}
                <CloudflareTurnstile key={`admin-choice-${turnstileResetKey}`} action="login" onTokenChange={setTurnstileToken} />
                <Button type="button" variant="ghost" size="sm" className="w-full text-muted-foreground text-xs" onClick={() => { setStep("login"); setError(""); }}>
                  <ArrowLeft size={14} className="mr-1" /> Volver al login
                </Button>
              </div>
            ) : step === "device_destination_choice" ? (
              <div className="space-y-4" data-testid="device-destination-choice">
                {error && <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm">{error}</div>}
                <div className="p-5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-center space-y-2">
                  <ShieldCheck size={40} weight="duotone" className="text-amber-400 mx-auto" />
                  <p className="text-sm font-semibold text-amber-300">Dispositivo nuevo detectado</p>
                  <p className="text-xs text-amber-300/80 leading-relaxed">¿A qué correo quieres enviar el link de aprobación?</p>
                </div>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 p-3 rounded-md bg-secondary/40 border border-border cursor-pointer hover:bg-secondary/60 transition-colors">
                    <input
                      type="checkbox"
                      checked={choicePrimarySelected}
                      onChange={(e) => setChoicePrimarySelected(e.target.checked)}
                      disabled={!choicePrimaryAvailable || choiceSubmitting}
                      className="w-4 h-4 accent-primary"
                      data-testid="choice-primary"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-semibold">Correo principal</p>
                      <p className="text-xs text-muted-foreground font-mono">{approvalPrimary || "—"}</p>
                    </div>
                  </label>
                  <label className={`flex items-center gap-3 p-3 rounded-md bg-secondary/40 border border-border cursor-pointer hover:bg-secondary/60 transition-colors ${!choiceSecondaryAvailable ? "opacity-50 cursor-not-allowed" : ""}`}>
                    <input
                      type="checkbox"
                      checked={choiceSecondarySelected}
                      onChange={(e) => setChoiceSecondarySelected(e.target.checked)}
                      disabled={!choiceSecondaryAvailable || choiceSubmitting}
                      className="w-4 h-4 accent-primary"
                      data-testid="choice-secondary"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-semibold">Correo secundario</p>
                      <p className="text-xs text-muted-foreground font-mono">
                        {choiceSecondaryAvailable ? approvalSecondary : "No configurado · configúralo en /admin/security"}
                      </p>
                    </div>
                  </label>
                </div>
                <Button type="button" onClick={submitDestinationChoice} disabled={choiceSubmitting || (!choicePrimarySelected && !choiceSecondarySelected)} className="w-full" data-testid="submit-destination-choice">
                  {choiceSubmitting ? <CircleNotch size={16} className="mr-2 animate-spin" /> : <EnvelopeSimple size={16} className="mr-2" />}
                  Enviar link de aprobación
                </Button>
                <Button type="button" variant="ghost" size="sm" className="w-full text-muted-foreground text-xs" onClick={() => { setStep("login"); setError(""); setChoiceToken(""); }} data-testid="choice-cancel">
                  <ArrowLeft size={14} className="mr-1" /> Volver al login
                </Button>
              </div>
            ) : step === "device_approval" ? (
              <div className="space-y-4" data-testid="device-approval">
                {error && <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm">{error}</div>}
                {approvalStatus === "pending" && (
                  <>
                    <div className="p-5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-center space-y-2">
                      <ShieldCheck size={40} weight="duotone" className="text-amber-400 mx-auto" />
                      <p className="text-sm font-semibold text-amber-300">
                        {approvalGoesToFounder ? "Esperando aprobación del Administrador" : "Dispositivo nuevo detectado"}
                      </p>
                      <p className="text-xs text-amber-300/80 leading-relaxed">
                        {approvalGoesToFounder ? (
                          <>El correo de aprobación se envió al <strong>administrador del sistema</strong>:<br /></>
                        ) : (
                          <>Por seguridad enviamos un correo de aprobación a:<br /></>
                        )}
                        <span className="font-mono text-amber-200">{approvalPrimary}</span>
                        {approvalSecondary && (<><br /><span className="font-mono text-amber-200/80">{approvalSecondary}</span></>)}
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary/40 border border-border space-y-2">
                      <div className="flex items-center gap-3">
                        <CircleNotch size={20} className="animate-spin text-primary" />
                        <p className="text-sm font-semibold">
                          {approvalGoesToFounder ? "Esperando que el administrador apruebe..." : "Esperando tu aprobación..."}
                        </p>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {approvalGoesToFounder ? (
                          <>Pide al administrador que abra su correo y autorice tu acceso, o que te apruebe desde su panel de seguridad. Esta pantalla detectará la aprobación en segundos y te llevará al CRM. El enlace expira en 30 minutos.</>
                        ) : (
                          <>Abre el correo y haz clic en <strong className="text-primary">SOY YO · Aprobar</strong>. Esta pantalla detectará la aprobación automáticamente y te llevará al CRM. El enlace expira en 30 minutos.</>
                        )}
                      </p>
                    </div>
                  </>
                )}
                {approvalStatus === "approved" && (
                  <div className="p-5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-center space-y-2">
                    <ShieldCheck size={40} weight="duotone" className="text-emerald-400 mx-auto" />
                    <p className="text-sm font-semibold text-emerald-300">Aprobado · Entrando al CRM...</p>
                  </div>
                )}
                {approvalStatus === "denied" && (
                  <div className="p-5 rounded-lg bg-destructive/10 border border-destructive/30 text-center space-y-2">
                    <p className="text-sm font-semibold text-destructive">Acceso bloqueado por el Founder</p>
                    <p className="text-xs text-destructive/80">Cambia tu contraseña inmediatamente si no fuiste tú.</p>
                  </div>
                )}
                {approvalStatus === "expired" && (
                  <div className="p-5 rounded-lg bg-destructive/10 border border-destructive/30 text-center space-y-2">
                    <p className="text-sm font-semibold text-destructive">Enlace expirado</p>
                    <p className="text-xs text-muted-foreground">Vuelve al login para generar un nuevo enlace.</p>
                  </div>
                )}
                <Button type="button" variant="ghost" size="sm" className="w-full text-muted-foreground text-xs" onClick={() => { setStep("login"); setError(""); setApprovalToken(""); setApprovalStatus("pending"); }} data-testid="device-approval-cancel">
                  <ArrowLeft size={14} className="mr-1" /> Volver al login
                </Button>
              </div>
            ) : step === "totp" ? (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="totp-form">
                {error && <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm" data-testid="login-error">{error}</div>}
                <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/20 text-center">
                  <Key size={32} weight="duotone" className="text-purple-400 mx-auto mb-2" />
                  <p className="text-sm text-purple-300">{useBackupCode ? "Codigo de Respaldo" : "Google Authenticator"}</p>
                  <p className="text-xs text-purple-300/70">{useBackupCode ? "Ingrese uno de sus codigos de respaldo (XXXX-XXXX)" : "Ingrese el codigo de su app de autenticacion"}</p>
                </div>
                <div className="space-y-2">
                  {useBackupCode ? (
                    <Input
                      type="text"
                      maxLength={10}
                      placeholder="XXXX-XXXX"
                      value={verificationCode}
                      onChange={(e) => {
                        const v = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, "");
                        setVerificationCode(v);
                      }}
                      className="bg-secondary border-border text-center text-xl font-mono tracking-[0.25em] h-14 uppercase"
                      autoFocus
                      required
                      data-testid="totp-backup-input"
                    />
                  ) : (
                    <Input type="text" inputMode="numeric" maxLength={6} placeholder="000000" value={verificationCode} onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ""))} className="bg-secondary border-border text-center text-2xl font-mono tracking-[0.5em] h-14" autoFocus required data-testid="totp-code-input" />
                  )}
                </div>
                <CloudflareTurnstile key={`totp-${turnstileResetKey}`} action="login" onTokenChange={setTurnstileToken} />
                <Button type="submit" className="w-full bg-primary hover:bg-primary/90 font-semibold" disabled={loading || (TURNSTILE_ENABLED && !turnstileToken) || (useBackupCode ? verificationCode.replace(/-/g,"").length < 8 : verificationCode.length !== 6)} data-testid="totp-submit-button">
                  {loading ? <CircleNotch size={18} className="mr-2 animate-spin" /> : <ShieldCheck size={18} className="mr-2" />} Verificar
                </Button>
                <Button type="button" variant="ghost" size="sm" className="w-full text-muted-foreground text-xs" onClick={() => { setUseBackupCode(!useBackupCode); setVerificationCode(""); setError(""); }} data-testid="toggle-backup-code">
                  <Key size={14} className="mr-1" /> {useBackupCode ? "Usar codigo de Authenticator" : "Usar codigo de respaldo"}
                </Button>
                <Button type="button" variant="ghost" size="sm" className="w-full text-muted-foreground text-xs" onClick={() => { setStep("login"); setVerificationCode(""); setUseBackupCode(false); setError(""); }}>
                  <ArrowLeft size={14} className="mr-1" /> Volver al login
                </Button>
              </form>
            ) : step === "2fa_prompt" ? (
              <div className="space-y-4" data-testid="2fa-prompt">
                {error && <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm" data-testid="login-error">{error}</div>}
                <div className="p-5 rounded-lg bg-primary/10 border border-primary/20 text-center space-y-2">
                  <ShieldCheck size={40} weight="duotone" className="text-primary mx-auto" />
                  <p className="text-sm font-semibold">Dispositivo no reconocido</p>
                  <p className="text-xs text-muted-foreground">Seleccione a dónde enviar el código de verificación</p>
                </div>

                {/* Option 1: send to user's email */}
                <CloudflareTurnstile key={`2fa-prompt-${turnstileResetKey}`} action="login" onTokenChange={setTurnstileToken} />
                <Button
                  className="w-full bg-primary hover:bg-primary/90 font-semibold h-auto py-3 flex-col items-start gap-0.5"
                  onClick={() => handleStaffSendCode("user")}
                  disabled={loading || sendsRemaining <= 0 || (TURNSTILE_ENABLED && !turnstileToken)}
                  data-testid="send-code-user-btn"
                >
                  <div className="flex items-center gap-2 w-full">
                    <EnvelopeSimple size={16} />
                    <span className="text-sm">Al correo del usuario</span>
                  </div>
                  <span className="text-[11px] opacity-80 font-mono pl-6">{maskedEmail}</span>
                </Button>


                <Button type="button" variant="ghost" className="w-full text-muted-foreground" onClick={() => { setStep("login"); setError(""); }} data-testid="cancel-2fa-btn">
                  Cancelar
                </Button>
                {sendsRemaining <= 0 && (
                  <p className="text-xs text-center text-destructive">Ha alcanzado el límite de envíos. Intente nuevamente en 15 minutos.</p>
                )}
              </div>
            ) : step === "2fa" ? (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="2fa-form">
                {error && (
                  <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm" data-testid="login-error">{error}</div>
                )}
                <div className="p-4 rounded-lg bg-primary/10 border border-primary/20 text-center">
                  <ShieldCheck size={32} weight="duotone" className="text-primary mx-auto mb-2" />
                  <p className="text-sm text-primary">Codigo de verificacion enviado a</p>
                  <p className="text-sm font-bold font-mono text-primary">{maskedEmail}</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Codigo de 6 digitos</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="000000"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ""))}
                    className="bg-secondary border-border text-center text-2xl font-mono tracking-[0.5em] h-14"
                    autoFocus
                    required
                    data-testid="2fa-code-input"
                  />
                </div>
                <CloudflareTurnstile key={`2fa-${turnstileResetKey}`} action="login" onTokenChange={setTurnstileToken} />
                <Button type="submit" className="w-full bg-primary hover:bg-primary/90 font-semibold" disabled={loading || (TURNSTILE_ENABLED && !turnstileToken) || verificationCode.length !== 6} data-testid="2fa-submit-button">
                  {loading ? <CircleNotch size={18} className="mr-2 animate-spin" /> : <ShieldCheck size={18} className="mr-2" />}
                  Verificar
                </Button>
                <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="trust-device-checkbox">
                  <input type="checkbox" checked={trustDevice} onChange={(e) => setTrustDevice(e.target.checked)} className="w-4 h-4 rounded border-border accent-primary" />
                  <span className="text-sm text-muted-foreground">Confiar en este equipo (7 dias)</span>
                </label>
                <div className="flex items-center justify-between text-xs">
                  <Button type="button" variant="ghost" size="sm" className="text-muted-foreground" onClick={() => { setStep("login"); setVerificationCode(""); setError(""); }}>
                    <ArrowLeft size={14} className="mr-1" /> Volver
                  </Button>
                  <Button type="button" variant="ghost" size="sm" className={sendsRemaining > 0 ? "text-primary" : "text-muted-foreground"} onClick={handleResendCode} disabled={loading || sendsRemaining <= 0 || (TURNSTILE_ENABLED && !turnstileToken)} data-testid="resend-code-button">
                    {sendsRemaining > 0 ? `Reenviar codigo (${sendsRemaining} restante${sendsRemaining > 1 ? "s" : ""})` : "Limite alcanzado"}
                  </Button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
                  {licenseBlocked && (
                    <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 space-y-2" data-testid="license-blocked-banner">
                      <div className="flex items-center gap-2 mb-1">
                        <ShieldCheck size={18} weight="duotone" className="text-red-400" />
                        <p className="text-sm font-bold text-red-300 tracking-wide uppercase">Licencia Vencida</p>
                      </div>
                      <p className="text-sm text-red-100/90 leading-relaxed">
                        Su acceso al CRM <strong>Commandix</strong> ha sido suspendido por falta de pago de la licencia semanal.
                      </p>
                      <p className="text-xs text-red-200/70 leading-relaxed">
                        Por favor, comuníquese con su administrador para regularizar el pago y reactivar el servicio.
                      </p>
                    </div>
                  )}
                  {error && !licenseBlocked && (
                    <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm" data-testid="login-error">{error}</div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium">Correo Electronico</Label>
                    <div className="relative">
                      <EnvelopeSimple size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input id="email" type="email" placeholder="correo@ejemplo.com" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-10 bg-secondary border-border" required data-testid="login-email-input" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-sm font-medium">Contrasena</Label>
                    <div className="relative">
                      <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input id="password" type={showPassword ? "text" : "password"} placeholder="Contrasena" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-10 pr-10 bg-secondary border-border" required data-testid="login-password-input" />
                      <button
                        type="button"
                        onClick={() => setShowPassword(v => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                        data-testid="login-password-toggle"
                      >
                        {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                  <CloudflareTurnstile key={`login-${turnstileResetKey}`} action="login" onTokenChange={setTurnstileToken} />
                  <Button type="submit" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold" disabled={loading || (TURNSTILE_ENABLED && !turnstileToken)} data-testid="login-submit-button">
                    {loading ? <CircleNotch size={18} className="mr-2 animate-spin" /> : null}
                    {loading ? "Ingresando..." : "Ingresar"}
                  </Button>
                  <div className="text-center pt-2 space-y-1">
                    <button
                      type="button"
                      onClick={() => navigate(forgotPasswordHref)}
                      className="text-xs text-muted-foreground hover:text-primary transition-colors underline-offset-2 hover:underline"
                      data-testid="login-forgot-password-link"
                    >
                      ¿Olvidaste tu contraseña?
                    </button>
                    {safeNext && (
                      <p className="text-[11px] text-white/35" data-testid="login-next-hint">
                        Al recuperar tu acceso, volverás a tu registro pendiente.
                      </p>
                    )}
                  </div>
                </form>
              </div>
            )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
